// useExpandedNonWorkingDays.js - Hook for managing expanded non-working days (public holidays +
// closing days) with lazy loading
import { useState, useCallback, useRef, useMemo } from 'react';
import { nonWorkingDaysAPI } from '../../../features/settings/services/nonWorkingDays';
import { formatDateToLocalString } from '../../../i18n/dateUtils';

/**
 * Format date to YYYY-MM-DD string in local timezone
 * Using the shared utility to avoid timezone offset issues
 */
const formatDateToString = formatDateToLocalString;

/**
 * Check if a range is already covered by existing fetched ranges
 */
const isRangeCovered = (startDate, endDate, fetchedRanges) => {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();

  for (const range of fetchedRanges) {
    if (range.start <= start && range.end >= end) {
      return true;
    }
  }
  return false;
};

/**
 * Merge overlapping ranges for efficient tracking
 */
const mergeRanges = (ranges) => {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const current = sorted[i];

    // If ranges overlap or are adjacent, merge them
    if (current.start <= last.end + 86400000) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push(current);
    }
  }

  return merged;
};

/**
 * Hook for managing expanded non-working days with lazy loading and caching
 *
 * Stores non-working days as Map<dateString, {name, kind, is_full_day}[]>
 * since a single date can have multiple entries (e.g., a recurring closing-day
 * rule and a public holiday, or an overlapping date range, can land on the
 * same day). Entries carry `kind` (PUBLIC_HOLIDAY | CLOSING_DAY) so consumers
 * like the Gantt overlay can distinguish them visually — the API response
 * already includes it, this hook just wasn't threading it through before.
 */
export const useExpandedNonWorkingDays = () => {
  // Map: date string (YYYY-MM-DD) -> array of {name, kind, is_full_day}
  const [nonWorkingDaysMap, setNonWorkingDaysMap] = useState(new Map());
  // Track which date ranges have been fetched
  const fetchedRangesRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch expanded non-working days for a date range and merge with existing data
   */
  const fetchExpandedNonWorkingDays = useCallback(async (startDate, endDate) => {
    const startStr = formatDateToString(startDate);
    const endStr = formatDateToString(endDate);
    const startTime = new Date(startDate).getTime();
    const endTime = new Date(endDate).getTime();

    // Check if this range is already covered
    if (isRangeCovered(startDate, endDate, fetchedRangesRef.current)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await nonWorkingDaysAPI.getExpanded(startStr, endStr);
      const response = result.data;

      if (response?.non_working_days) {
        setNonWorkingDaysMap((prevMap) => {
          const newMap = new Map(prevMap);

          response.non_working_days.forEach((entry) => {
            const dateKey = entry.date;
            const existingEntries = newMap.get(dateKey) || [];

            // Add if not already present (by name) for this date
            if (!existingEntries.some((e) => e.name === entry.name)) {
              newMap.set(dateKey, [
                ...existingEntries,
                { name: entry.name, kind: entry.kind, is_full_day: entry.is_full_day },
              ]);
            }
          });

          return newMap;
        });

        // Track that we've fetched this range
        fetchedRangesRef.current = mergeRanges([
          ...fetchedRangesRef.current,
          { start: startTime, end: endTime },
        ]);
      }
    } catch (err) {
      console.error('Failed to fetch expanded non-working days:', err);
      setError(err.message || 'Failed to fetch non-working days');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check if a specific date is a non-working day
   */
  const isNonWorkingDay = useCallback((date) => {
    const dateStr = formatDateToString(date);
    return nonWorkingDaysMap.has(dateStr);
  }, [nonWorkingDaysMap]);

  /**
   * Get the full non-working day entries ({name, kind, is_full_day}) for a
   * specific date. Returns null if not a non-working day.
   */
  const getNonWorkingDayEntries = useCallback((date) => {
    const dateStr = formatDateToString(date);
    return nonWorkingDaysMap.get(dateStr) || null;
  }, [nonWorkingDaysMap]);

  /**
   * Get non-working day names for a specific date
   * Returns array of names or null if not a non-working day
   */
  const getNonWorkingDayNames = useCallback((date) => {
    const entries = getNonWorkingDayEntries(date);
    return entries ? entries.map((e) => e.name) : null;
  }, [getNonWorkingDayEntries]);

  /**
   * Get all non-working days as an array of { date, entries }
   */
  const nonWorkingDaysList = useMemo(() => {
    return Array.from(nonWorkingDaysMap.entries()).map(([date, entries]) => ({
      date,
      entries,
    }));
  }, [nonWorkingDaysMap]);

  /**
   * Clear all cached non-working days
   */
  const clearCache = useCallback(() => {
    setNonWorkingDaysMap(new Map());
    fetchedRangesRef.current = [];
    setError(null);
  }, []);

  return {
    nonWorkingDaysMap,
    nonWorkingDaysList,
    loading,
    error,
    fetchExpandedNonWorkingDays,
    isNonWorkingDay,
    getNonWorkingDayNames,
    getNonWorkingDayEntries,
    clearCache,
  };
};
